import gql from 'graphql-tag';
import { FolderSchema } from '../../definitions/schemas';
import Interface from '../Interface';

/**
 * Input for creating a folder
 * Folders are created as drafts (no roomId) and associated via createRoom/updateRoom
 */
export interface CreateFolderInput {
  name: string;
  parentFolderId?: number;
}

interface CreateFolderData {
  createFolder: {
    folder: FolderSchema;
    errors?: string[];
    message?: string;
  };
}

/**
 * Input for updating a folder
 * CRITICAL: Called after every content add/remove/reorder
 */
export interface UpdateFolderInput {
  folderId: string;
  name?: string;
  structure?: {
    items: Array<{
      id: string;
      type: 'content' | 'text_box' | 'folder';
      name?: string;
      description?: string;
      coverUri?: string;
      mediaType?: string;
      text?: string;
      position?: number;
    }>;
  };
}

interface UpdateFolderData {
  updateFolder: {
    folder: FolderSchema;
    errors?: string[];
    message?: string;
  };
}

export default class FoldersInterface extends Interface {
  /**
   * Creates a draft folder (not associated with any room)
   * Folder will be associated with room via createRoom/updateRoom folders parameter
   */
  public async create(input: CreateFolderInput) {
    return this.mutate<CreateFolderData>(
      async () => {
        const response = await this.apolloService.executeMutation({
          mutation: gql`
            mutation createFolder($input: CreateFolderInput!) {
              createFolder(input: $input) {
                errors
                message
                folder {
                  id
                  name
                  organizationId
                  roomId
                  templateId
                  parentFolderId
                  structure
                  createdById
                  updatedById
                  deleted
                  createdAt
                  updatedAt
                  depth
                  parentFolder {
                    id
                    name
                  }
                  childFolders {
                    id
                    name
                  }
                  room {
                    id
                    name
                    status
                  }
                  createdBy {
                    id
                    email
                    firstName
                    lastName
                    fullName
                  }
                }
              }
            }
          `,
          variables: { input },
        });

        if (response.errors?.length || !response.data?.createFolder) {
          return {
            data: response.data,
            serverErrors: response.errors ?? [
              { message: 'Error creating folder' },
            ],
            serverMessage: undefined,
          };
        }

        const { errors, message } = response.data.createFolder;

        return {
          data: response.data,
          serverErrors: errors,
          serverMessage: message,
        };
      },
      'Error creating folder',
      true
    );
  }

  /**
   * Updates folder structure
   *
   * @deprecated MIGRATION (DSR-4516): This method calls the `updateFolder` mutation
   * which has been REMOVED from the backend. Use the `folders` parameter in
   * createRoom/updateRoom instead. See FolderRoomIntegration.md for migration guide.
   *
   * This method will throw an error if called after backend is deployed.
   *
   * Old flow: createFolder → updateFolder (for each change)
   * New flow: createFolder → track changes in state → createRoom/updateRoom with folders array
   */
  public async update(input: UpdateFolderInput) {
    return this.mutate<UpdateFolderData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation updateFolder($input: UpdateFolderInput!) {
            updateFolder(input: $input) {
              errors
              message
              folder {
                id
                name
                structure
                updatedAt
              }
            }
          }
        `,
        variables: { input },
      });

      const { errors, message } = response.data.updateFolder;

      return {
        data: response.data,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error updating folder');
  }

  /**
   * Deletes a folder
   * Backend will handle moving contents to parent/root
   */
  public async delete(folderId: string) {
    return this.mutate<null>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation deleteFolder($input: DeleteFolderInput!) {
            deleteFolder(input: $input) {
              errors
              message
            }
          }
        `,
        variables: {
          input: { folderId },
        },
      });

      const { errors, message } = response.data.deleteFolder;

      return {
        data: null,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error deleting folder');
  }
}
